import { useMemo, useEffect } from "react";
import { formatCurrencyForTable, formatPercentageForTable } from "../functions/quoteFunctions";
import { itemStats } from "../functions/quoteOverviewCalculations";
import DataTableMatrix from "../../../components/DataTableMatrix";

const QuoteOverviewGeneralYearlyBreakdownTable = ({quoteItems, setExportData}) => {

    const allQuoteItemStats = useMemo(() => itemStats(quoteItems), [quoteItems]);
    const maxTermLength = useMemo(() => Math.max(...quoteItems.map(i => i.termLengthMonths)), [quoteItems]);
    const maxYears = Math.ceil(maxTermLength / 12) || 1;
   
    const yearlyQuoteItemStats = useMemo(() => {
        const rows = [];
        let stats = {}

        for (let year = 1; year <= maxYears; year++) {
            rows.push({
                key: `year${year}`,
                label: `Year ${year}`
            });
            stats[ `year${year}` ] = itemStats(quoteItems, year);
        }
        return stats;
    }, [quoteItems, maxYears]);

    const rows = useMemo(() => Object.keys(yearlyQuoteItemStats).map(yearKey =>   ({
        key: yearKey,
        label: yearKey.replace("year", "Year ")
    })), [yearlyQuoteItemStats]);

    const cols = useMemo(() => [
        {
            key: "revenue",
            label: "Revenue",
            dataType: "currency",
            render: function (row, col) {
                return formatCurrencyForTable(yearlyQuoteItemStats[row]?.["totalUpfrontAndRecurringPrice"] || 0);
            }
        },
        {
            key: "gm",
            label: "GM",
            dataType: "currency",
            render: function (row, col) {
                return formatCurrencyForTable(yearlyQuoteItemStats[row]?.["totalUpfrontAndRecurringMargin"] || 0);
            }
        },
        {
            key: "gmPercent",
            label: "GM %",
            dataType: "percentage",
            render: function (row, col) {
                return formatPercentageForTable(yearlyQuoteItemStats[row]?.["totalUpfrontAndRecurringMarginPercentage"] || 0);
            }
        }
    ], [yearlyQuoteItemStats]);

    const matrixData = useMemo(() => {
        const data = {};        

        rows.forEach(row => {
            data[row.key] = {};

            cols.forEach(col => {
                data[row.key][col.key] = {
                    render: col.render
                };
            });
        });

        return data;
    }, [yearlyQuoteItemStats, cols]);

    const footerValuesForExport = useMemo(() => ([
        allQuoteItemStats.totalUpfrontAndRecurringPrice,
        allQuoteItemStats.totalUpfrontAndRecurringMargin,
        allQuoteItemStats.totalUpfrontAndRecurringMarginPercentage
    ]), [allQuoteItemStats]);

    const footerValuesForDisplay = useMemo(() => ([
        formatCurrencyForTable(footerValuesForExport[0], 0),
        formatCurrencyForTable(footerValuesForExport[1], 0),
        formatPercentageForTable(footerValuesForExport[2], 0)
    ]), [footerValuesForExport]);

    
   const convertDataTableToExportFormat = (matrixData, footerValuesForExport) => {
            const rowsForExport = [];
    
            rowsForExport.push([{value: "", type:"text", style: "header", borderBottom: true, borderRight: true}, ...cols.map(col => ({ value: col.label, type: "text", style: "header", borderBottom: true, align: "right" }))]);
    
            for (const row of rows) {
                if (matrixData[row.key] !== undefined) {
                    console.log("Exporting row", row, matrixData[row.key]);
                        rowsForExport.push(
                            [{value: row.label, type:"text", style: "header", borderRight: true}, ...(cols.map(column => ({
                                value: matrixData[row.key][column.key].render ? matrixData[row.key][column.key].render(row.key, column.key) : matrixData[row.key][column.key].value,
                            dataType: column.dataType || "text"
                        })))]
                    );
                }
            }

            rowsForExport.push([
                {value: "TOTAL:", align:"right", type:"text", style: "header", borderTop: true, borderRight: true},
                { value: footerValuesForExport[0].toString(), dataType: "currency", align: "right", style: "footer", borderTop: true },
                { value: footerValuesForExport[1].toString(), dataType: "currency", align: "right", style: "footer", borderTop: true },
                { value: footerValuesForExport[2].toString(), dataType: "percentage", align: "right", style: "footer", borderTop: true }
            ]);

            return rowsForExport;
        };
        
        useEffect(() => {      
     
        if (Object.keys(matrixData).length > 0) {
            setExportData(prev => {
                if (!matrixData) return prev;

                const otherSheets = prev?.sheets?.filter(sheet => sheet.id !== "general") || [];
                const newGeneralTabData = {
                    id: "general",
                    name: "General",
                    sortOrder: 1,
                    tables: (prev?.sheets?.find(sheet => sheet.id === "general")?.tables || []).filter(t => t.header !== "Yearly Breakdown")
                }

                newGeneralTabData.tables.push({
                    header: "Yearly Breakdown",
                    sortOrder: 3,
                    rows: convertDataTableToExportFormat(matrixData, footerValuesForExport),
                });

                return (
                {
                    ...prev,
                    sheets: [
                        ...otherSheets,
                        newGeneralTabData
                    ]
                })

            });
        } else {
            console.log("No matrix data to export", matrixData, Object.keys(matrixData));
        }
    }, [matrixData, footerValuesForExport, setExportData]);
    

    return (
        <>
            <DataTableMatrix
                name="yearly-breakdown"
                label="Yearly Breakdown"
                columns={cols}
                rows={rows}
                data={matrixData}
                footerValues={footerValuesForDisplay}
                width="auto"
            />
        </>
    );
}

export default QuoteOverviewGeneralYearlyBreakdownTable;